import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ApiKeyState {
    apiKey: string | null;
    maskedApiKey: string | null;
    isConfigured: boolean;
}

export type ApiKeyPanelMode = 'setup' | 'summary' | 'edit';

export interface ApiKeyPanelState {
    isOpen: boolean;
    mode: ApiKeyPanelMode;
}

const INITIAL_API_KEY_STATE: ApiKeyState = {
    apiKey: null,
    maskedApiKey: null,
    isConfigured: false,
};

const INITIAL_PANEL_STATE: ApiKeyPanelState = {
    isOpen: false,
    mode: 'setup',
};

@Injectable({ providedIn: 'root' })
export class ApiKeyStoreService {
    private readonly apiKeyStateSubject = new BehaviorSubject<ApiKeyState>(
        INITIAL_API_KEY_STATE
    );
    private readonly panelStateSubject = new BehaviorSubject<ApiKeyPanelState>(
        INITIAL_PANEL_STATE
    );

    readonly apiKeyState$ = this.apiKeyStateSubject.asObservable();
    readonly panelState$ = this.panelStateSubject.asObservable();

    setApiKey(apiKey: string): void {
        const trimmedApiKey = apiKey.trim();
        this.apiKeyStateSubject.next({
            apiKey: trimmedApiKey,
            maskedApiKey: this.maskApiKey(trimmedApiKey),
            isConfigured: trimmedApiKey.length > 0,
        });
    }

    getApiKey(): string | null {
        return this.apiKeyStateSubject.value.apiKey;
    }

    getMaskedApiKey(): string | null {
        return this.apiKeyStateSubject.value.maskedApiKey;
    }

    hasApiKey(): boolean {
        return this.apiKeyStateSubject.value.isConfigured;
    }

    clearApiKey(): void {
        this.apiKeyStateSubject.next(INITIAL_API_KEY_STATE);
    }

    openPanel(mode?: ApiKeyPanelMode): void {
        this.panelStateSubject.next({
            isOpen: true,
            mode: mode ?? (this.hasApiKey() ? 'summary' : 'setup'),
        });
    }

    closePanel(): void {
        this.panelStateSubject.next({
            ...this.panelStateSubject.value,
            isOpen: false,
        });
    }

    private maskApiKey(apiKey: string): string {
        if (!apiKey) {
            return '';
        }

        const visibleChars = 4;
        const mask = '••••••••••••••••••••';

        if (apiKey.length <= visibleChars) {
            return '••••';
        }

        return `${mask}${apiKey.slice(-visibleChars)}`;
    }
}
